/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MetricsModule */

import { Injectable } from '@angular/core';

import { MetricUnits } from 'generated-types';

const oneForOne = {
    METRIC_COUNT: '',
    PER_SECOND: '/sec',
    SECONDS: 'sec',
    MILLISECONDS: 'ms',
    PERCENT: '%',
};

const scales = [
    'KILO',
    'MEGA',
    'GIGA',
    'TERA',
    'PETA',
];

const byteOrBit = {
    BYTES: 'B',
    BITS: 'b',
};

const secondHalf = {
    _PER_SECOND: 'ps',
};

/**
 * The very atomic piece of metrics data.
 * Unit deserves an independent property since in the UI it's usually rendered separately.
 */
export interface IMetricsValue {
    value: string | number;
    unit?: string;
}

export const CONVERT_SERVICE_TOKEN = 'Convert';

/**
 * @description
 *     Scale values and units to the largest possible size.
 * @author Alex Malitsky, Zhiqian Liu
 */
@Injectable({
    providedIn: 'root',
})
export class ConvertService {
    /**
     * Scale the value and get the pretty version of the units.
     * @param value The value to scale
     * @param unit The unit to scale and/or convert
     * @param scaleIfBigger The largest the value can be before scaling, defaults to 1,000
     * @param divisor How much to divide the value to, defaults to 1,024
     * @return hash with new value and unit, ex: { value: 123, unit: 'Kbps' }
     */
    public getVal(
        value: string | number,
        unit: string,
        scaleIfBigger?: number,
        divisor?: number,
    ): IMetricsValue {
        unit = unit.toUpperCase();

        // Set defaults
        if (scaleIfBigger === undefined) {
            scaleIfBigger = 1000;
        }

        if (divisor === undefined) {
            divisor = 1024;
        }

        // Determine if it is a simple unit conversion (never scales)
        if (oneForOne[unit] !== undefined) {
            return {
                value,
                unit: oneForOne[unit],
            };
        }

        // Divide number while converting the unit until value < scaleIfBigger
        while (+value >= scaleIfBigger) {
            (value as number) /= divisor;
            unit = this.convertUp(unit);
        }

        // return new value and pretty units
        return {
            value,
            unit: this.createPrettyUnit(unit),
        };
    }

    /**
     * Remove 'ps' or '/sec' from the end of a unit for the use of presenting sum value.
     * eg. bps -> b, Bps -> B, Kbps -> Kb, MBps -> MB, /sec -> ''.
     *
     * When displaying sum values, since there's only one unit given by the API response (say only
     * `bps` is present for the regular data, for sum data there's no another unit specified), we
     * infer the correct unit from the rate unit.
     * eg.  if summation is to be shown for data related to `MBps`, then `MB` should be its unit.
     */
    // eslint-disable-next-line class-methods-use-this
    public getUnitForSum(unit: string | undefined): string | undefined {
        return unit?.replace(/ps|\/sec/, '');
    }

    /**
     * Convert the unit one scale up.
     * eg. KILO_BYTES -> MEGA_BYTES
     */
    // eslint-disable-next-line class-methods-use-this
    private convertUp(unit: string): string {
        let parts;

        if (unit.indexOf('BITS') === 0 || unit.indexOf(MetricUnits.BYTES) === 0) {
            return `${scales[0]}_${unit}`;
        } else if (unit.indexOf('BITS') > 0) {
            parts = unit.split('_BITS');

            return `${scales[scales.indexOf(parts[0]) + 1]}_BITS${parts[1]}`;
        } else if (unit.indexOf(MetricUnits.BYTES) > 0) {
            parts = unit.split('_BYTES');

            return `${scales[scales.indexOf(parts[0]) + 1]}_BYTES${parts[1]}`;
        } else {
            throw new Error('Unit must have BITS or BYTES');
        }
    }

    /**
     * Convert the unit to an abbreviated version.
     * eg. KILO_BYTES -> KB
     */
    // eslint-disable-next-line class-methods-use-this
    private createPrettyUnit(unit: string): string | undefined {
        const type = unit.indexOf(MetricUnits.BYTES) === -1 ? 'BITS' : MetricUnits.BYTES;
        const parts = unit.split(type);

        if (!type || parts.length !== 2) {
            throw new Error('Unit must have BITS or BYTES and only once');
        }

        if (parts[0] === '') {
            if (parts[1] === '') {
                return byteOrBit[type];
            } else {
                return byteOrBit[type] + secondHalf[parts[1]];
            }
        } else if (parts[1] === '') {
            // Get the first part, and the first character, KILO = K
            return parts[0][0] + byteOrBit[type];
        } else {
            return parts[0][0] + byteOrBit[type] + secondHalf[parts[1]];
        }
    }
}
